// src/services/requestAccessService.js
import { repo } from '../dbservice/repo';
import { sendAccessRequestAck } from './brevoEmailService';
import { RequestAccessModel } from '../models/RequestAccessModel';

/**
 * RequestAccessService
 */

// ─── Company Validation ───────────────────────────────────────────────────────

/**
 * Validates a Company ID against the companies collection.
 *
 * @param {string} companyId
 * @returns {Promise<{ valid: boolean, company?: { name: string, id: string } }>}
 */
export const validateCompanyId = async (companyId) => {
  if (!companyId?.trim()) return { valid: false };

  try {
    const results = await repo.fetchData(
      'companies',
      [
        { field: 'companyId', value: companyId.trim().toUpperCase() },
        { field: 'status',    value: 'Active' },
      ],
      [],
      1,
    );

    if (results.length > 0) {
      return {
        valid:   true,
        company: { name: results[0].name, id: results[0].id },
      };
    }

    return { valid: false };
  } catch (err) {
    console.error('[RequestAccessService] Company validation failed:', err);
    return { valid: false };
  }
};

// ─── Department Loader ────────────────────────────────────────────────────────

/**
 * Fetches all departments belonging to a given companyId.
 * departments is a per-company collection → repo routes to local Supabase
 * if configured, Firebase otherwise.
 *
 * @param {string} companyId
 * @returns {Promise<Array<{ id: string, name: string, departmentName: string }>>}
 */
export const fetchDepartmentsForCompany = async (companyId) => {
  if (!companyId?.trim()) return [];

  try {
    const results = await repo.fetchData(
      'departments',
      [{ field: 'companyId', value: companyId.trim().toUpperCase() }],
    );

    return results.map((doc) => ({
      id:             doc.id,
      name:           doc.departmentName || doc.name,
      departmentName: doc.departmentName || doc.name,
      ...doc,
    }));
  } catch (err) {
    console.error('[RequestAccessService] Department fetch failed:', err);
    return [];
  }
};

// ─── Form Validation ──────────────────────────────────────────────────────────

/**
 * Validates the user access request form.
 * @param {Object} formData
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateRequestAccessForm = (formData) => {
  const instance = new RequestAccessModel(
    {
      ...formData,
      
      role:      'user',
      status:    'pending',
    
      company:   formData.company || 'placeholder',
    
      location:  formData.city,
    },
    { isCreation: true },
  );

  if (!instance.isValid()) {
    const { name, email, companyId, countryCode, stateCode, city, address1, pincode } = formData;

    if (!name?.trim())      return { valid: false, error: 'Full name is required' };
    if (!email?.trim())     return { valid: false, error: 'Email address is required' };
    if (!/^\S+@\S+\.\S+$/.test(email.trim()))
                            return { valid: false, error: 'Please enter a valid email address' };
    if (!companyId?.trim()) return { valid: false, error: 'Company ID is required' };
    if (!/^[A-Z0-9]{3,20}$/.test(companyId.trim()))
                            return { valid: false, error: 'Company ID must be 3-20 alphanumeric characters' };
    if (!countryCode)       return { valid: false, error: 'Please select a country' };
    if (!stateCode)         return { valid: false, error: 'Please select a state' };
    if (!city?.trim())      return { valid: false, error: 'Please select a city' };
    if (!address1?.trim())  return { valid: false, error: 'Address line 1 is required' };
    if (!pincode?.trim())   return { valid: false, error: 'Pincode is required' };
    if (!/^\d{6}$/.test(pincode.trim()))
                            return { valid: false, error: 'Pincode must be exactly 6 digits' };

    return { valid: false, error: 'Invalid request data. Please review and try again.' };
  }

  return { valid: true };
};

// ─── Payload Builder ──────────────────────────────────────────────────────────

/**
 * Builds the clean access request document from raw form data.
 * Delegates shaping + trimming + alias mapping to RequestAccessModel.
 *
 * @param {Object} formData
 * @param {string} companyName  - resolved from validateCompanyId()
 * @param {string|null} userId  - Firebase UID of currently logged-in user
 * @returns {Object}
 */
const buildAccessRequestPayload = (formData, companyName, userId) => {
  const instance = new RequestAccessModel(
    {
      ...formData,
      company:     companyName,
      role:        'user',
      location:    formData.city,      // alias
      requestedBy: userId || null,
    },
    { isCreation: true },
  );

  return instance.toPayload();
};

// ─── Main Submission Entry Point ──────────────────────────────────────────────

/**
 * Full user access request submission pipeline:
 *   1. Validate form fields (model + string-mapped errors)
 *   2. Re-verify company is still active at submit time (async DB check)
 *   3. Build clean payload (model-validated)
 *   4. Guard-validate the shaped payload against the model again
 *   5. Persist via repo.upsertData() — routes to Firebase (central collection)
 *   6. Fire acknowledgment email (non-blocking, fire-and-forget)
 *
 * @param {Object} formData   - raw form state from the component
 * @param {string|null} userId - Firebase UID of currently logged-in user
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const submitAccessRequest = async (formData, userId = null) => {
  /* Step 1 — Sync field validation */
  const validation = validateRequestAccessForm(formData);
  if (!validation.valid) {
    return { success: false, error: validation.error };
  }

  /* Step 2 — Re-verify company at submit time */
  const companyCheck = await validateCompanyId(formData.companyId);
  if (!companyCheck.valid) {
    return {
      success: false,
      error: 'Invalid Company ID or company is not active. Please verify with your administrator.',
    };
  }

  /* Step 3 — Build payload via model */
  const payload = buildAccessRequestPayload(
    formData,
    companyCheck.company.name,
    userId,
  );

  /* Step 4 — Final guard validation before write */
  const guard = new RequestAccessModel(
    { ...payload, createdAt: new Date().toISOString() },
    { isCreation: true },
  );
  if (!guard.isValid()) {
    return {
      success: false,
      error: 'Submission failed validation. Please review your details.',
    };
  }

  /* Step 5 — Persist via generic repo method */
  const requestId = `user_req_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
  try {
    await repo.upsertData('accessRequests', requestId, guard.toPayload());
  } catch (err) {
    console.error('[RequestAccessService] Persistence failed:', err);
    return {
      success: false,
      error: err.message || 'Failed to submit request. Please try again.',
    };
  }

  /* Step 6 — Acknowledgment email (fire and forget) */
  sendAccessRequestAck({
    name:  payload.name,
    email: payload.email,
    role:  'user',
  }).catch((err) =>
    console.warn('[RequestAccessService] Acknowledgment email failed silently:', err),
  );

  return { success: true };
};